import { useState, useEffect } from 'react'
import { fmtDate, fmtDateTime } from '../../utils/format'
import DateInput from '../../components/DateInput'
import {
  PlusIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  CheckCircleIcon,
  CalendarIcon,
  UserIcon,
  BuildingOfficeIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline'

interface Zone {
  id: number
  name: string
  code: string
  description: string
  type: 'production' | 'storage' | 'preparation' | 'packaging' | 'other'
  type_name: string
  status: 'active' | 'inactive' | 'maintenance'
  status_name: string
  pending_cleaning_tasks_count: number
  overdue_cleaning_tasks_count: number
  overdue_cleaning_count?: number
  has_overdue_cleaning?: boolean
  has_overdue_tasks: boolean
}

interface ZoneElement {
  id: number
  element_type: string
  element_type_name: string
  name: string
  cleaning_frequency_hours: number
  readable_frequency: string
  priority: string
  priority_name: string
  status: string
  cleaning_status?: string
  is_cleaning_overdue: boolean
  next_cleaning_due?: string
  pending_tasks_count: number
}

interface Refrigerator {
  id: number
  name: string
  code: string
  type_name: string
  status_name: string
  cleaning_frequency_name?: string
  cleaning_status?: string
  next_cleaning_due?: string
  is_cleaning_overdue?: boolean
}

interface ZoneDetails extends Zone {
  elements: ZoneElement[]
  refrigerators: Refrigerator[]
}

interface CleaningRecord {
  id: number
  zone_id: number
  element_type?: string
  element_id?: number
  refrigerator_id?: number
  item_name: string
  item_type: 'element' | 'refrigerator'
  type: string
  description: string
  products_used?: string
  issues_found?: string
  solutions_applied?: string
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled'
  scheduled_date: string
  started_at?: string
  completed_at?: string
  cleaned_by?: string
  verification_notes?: string
  created_at: string
  updated_at: string
}

const API_BASE_URL = '/api/v1/dev'

export default function CleaningByZone() {
  const [zones, setZones] = useState<Zone[]>([])
  const [selectedZone, setSelectedZone] = useState<ZoneDetails | null>(null)
  const [cleaningRecords, setCleaningRecords] = useState<CleaningRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [zoneLoading, setZoneLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingRecord, setEditingRecord] = useState<CleaningRecord | null>(null)
  const [viewingRecord, setViewingRecord] = useState<CleaningRecord | null>(null)
  const [showViewModal, setShowViewModal] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dateFilter, setDateFilter] = useState<string>('')
  const [itemFilter, setItemFilter] = useState<string>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const perPage = 15

  // Form data
  const [formData, setFormData] = useState({
    item_type: 'element' as 'element' | 'refrigerator',
    element_id: '',
    refrigerator_id: '',
    type: 'routine',
    description: '',
    products_used: '',
    issues_found: '',
    solutions_applied: '',
    status: 'scheduled',
    scheduled_date: new Date().toISOString().split('T')[0],
    started_at: new Date().toISOString().split('T')[0],
    completed_at: new Date().toISOString().split('T')[0],
    cleaned_by: '',
    verification_notes: ''
  })

  // Fetch zones
  const fetchZones = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await fetch(`${API_BASE_URL}/zones`)
      const data = await response.json()

      if (response.ok) {
        setZones(data.data || [])
      } else {
        setError('Error al cargar zonas')
      }
    } catch (err) {
      setError('Error de conexión con el servidor')
      console.error('Error fetching zones:', err)
    } finally {
      setLoading(false)
    }
  }

  // Fetch zone details
  const fetchZoneDetails = async (zoneId: number) => {
    try {
      setZoneLoading(true)
      setError(null)
      const response = await fetch(`${API_BASE_URL}/zones/${zoneId}`)
      const data = await response.json()

      if (response.ok) {
        setSelectedZone(data.data)
        fetchCleaningRecords(zoneId)
      } else {
        setError('Error al cargar detalles de la zona')
      }
    } catch (err) {
      setError('Error de conexión con el servidor')
      console.error('Error fetching zone details:', err)
    } finally {
      setZoneLoading(false)
    }
  }

  // Fetch cleaning records for zone
  const fetchCleaningRecords = async (zoneId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/equipment-cleanings?zone_id=${zoneId}&per_page=100000`)
      const data = await response.json()

      if (response.ok) {
        setCleaningRecords(data.data || [])
      } else {
        console.error('Error loading cleaning records')
      }
    } catch (err) {
      console.error('Error fetching cleaning records:', err)
    }
  }

  useEffect(() => {
    fetchZones()
  }, [])

  // Reiniciar a la página 1 al cambiar los filtros
  useEffect(() => { setCurrentPage(1) }, [dateFilter, itemFilter])

  // Al cambiar de zona, limpiar filtros
  useEffect(() => { setDateFilter(''); setItemFilter('all') }, [selectedZone?.id])

  // Get status badge
  const getStatusBadge = (status: string) => {
    const statusConfig = {
      scheduled: { color: 'bg-blue-50 text-blue-700 border border-blue-200', icon: CalendarIcon, text: 'Programado' },
      in_progress: { color: 'bg-yellow-50 text-yellow-700 border border-yellow-200', icon: ClockIcon, text: 'En Progreso' },
      completed: { color: 'bg-green-50 text-green-700 border border-green-200', icon: CheckCircleIcon, text: 'Completado' },
      cancelled: { color: 'bg-red-50 text-red-700 border border-red-200', icon: XMarkIcon, text: 'Cancelado' }
    }

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.scheduled
    const Icon = config.icon

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.text}
      </span>
    )
  }

  // Estado de limpieza: inactivo / sin registros / al día / vencido
  const getCleaningStatusBadge = (status?: string) => {
    if (status === 'inactive') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-500 border border-gray-300">
          Inactivo
        </span>
      )
    }
    if (status === 'overdue') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
          <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
          Vencido
        </span>
      )
    }
    if (status === 'up_to_date') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
          <CheckCircleIcon className="w-3 h-3 mr-1" />
          Al día
        </span>
      )
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
        Sin registros
      </span>
    )
  }

  // Get priority badge
  const getPriorityBadge = (priority: string) => {
    const priorityConfig = {
      low: { color: 'bg-gray-50 text-gray-700 border border-gray-200', text: 'Baja' },
      medium: { color: 'bg-blue-50 text-blue-700 border border-blue-200', text: 'Media' },
      high: { color: 'bg-yellow-50 text-yellow-700 border border-yellow-200', text: 'Alta' },
      critical: { color: 'bg-red-50 text-red-700 border border-red-200', text: 'Crítica' }
    }

    const config = priorityConfig[priority as keyof typeof priorityConfig] || priorityConfig.medium

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    )
  }

  // Handle create cleaning record
  const openCreateModal = (itemType: 'element' | 'refrigerator', itemId: number, itemName: string) => {
    setEditingRecord(null)
    setFormData({
      item_type: itemType,
      element_id: itemType === 'element' ? itemId.toString() : '',
      refrigerator_id: itemType === 'refrigerator' ? itemId.toString() : '',
      type: 'routine',
      description: `Limpieza de ${itemName}`,
      products_used: '',
      issues_found: '',
      solutions_applied: '',
      status: 'completed',
      scheduled_date: new Date().toISOString().split('T')[0],
      started_at: new Date().toISOString().split('T')[0],
      completed_at: new Date().toISOString().split('T')[0],
      cleaned_by: '',
      verification_notes: ''
    })
    setShowCreateModal(true)
    setError(null)
  }

  // Handle edit cleaning record
  const openEditModal = (record: CleaningRecord) => {
    setEditingRecord(record)
    setFormData({
      item_type: record.item_type,
      element_id: record.element_id?.toString() || '',
      refrigerator_id: record.refrigerator_id?.toString() || '',
      type: record.type,
      description: record.description,
      products_used: record.products_used || '',
      issues_found: record.issues_found || '',
      solutions_applied: record.solutions_applied || '',
      status: record.status,
      scheduled_date: record.scheduled_date.split('T')[0],
      started_at: record.started_at?.split('T')[0] || '',
      completed_at: record.completed_at?.split('T')[0] || '',
      cleaned_by: record.cleaned_by || '',
      verification_notes: record.verification_notes || ''
    })
    setShowCreateModal(true)
    setError(null)
  }

  // Handle save
  const handleSave = async () => {
    if (!selectedZone) return

    setSaving(true)
    setError(null)

    try {
      const url = editingRecord
        ? `${API_BASE_URL}/equipment-cleanings/${editingRecord.id}`
        : `${API_BASE_URL}/equipment-cleanings`

      const method = editingRecord ? 'PUT' : 'POST'

      const payload = {
        zone_id: selectedZone.id,
        item_type: formData.item_type,
        element_id: formData.element_id ? parseInt(formData.element_id) : null,
        refrigerator_id: formData.refrigerator_id ? parseInt(formData.refrigerator_id) : null,
        type: formData.type,
        description: formData.description,
        products_used: formData.products_used || null,
        issues_found: formData.issues_found || null,
        solutions_applied: formData.solutions_applied || null,
        status: formData.status,
        scheduled_date: formData.scheduled_date,
        started_at: formData.started_at || null,
        completed_at: formData.completed_at || null,
        cleaned_by: formData.cleaned_by || null,
        verification_notes: formData.verification_notes || null
      }

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      const data = await response.json()

      if (response.ok) {
        fetchCleaningRecords(selectedZone.id)
        setShowCreateModal(false)
        setEditingRecord(null)
      } else {
        if (data.errors) {
          const messages = Object.values(data.errors).flat().join('\n')
          setError(messages)
        } else {
          setError(data.message || 'Error al guardar el registro de limpieza')
        }
      }
    } catch (err) {
      setError('Error de conexión con el servidor')
      console.error('Error saving cleaning record:', err)
    } finally {
      setSaving(false)
    }
  }

  // Handle view details
  const handleViewDetails = async (record: CleaningRecord) => {
    try {
      const response = await fetch(`${API_BASE_URL}/equipment-cleanings/${record.id}`)
      const data = await response.json()

      if (response.ok) {
        setViewingRecord(data.data)
        setShowViewModal(true)
      } else {
        setError('Error al cargar los detalles del registro')
      }
    } catch (err) {
      setError('Error de conexión con el servidor')
      console.error('Error fetching cleaning details:', err)
    }
  }

  // Handle delete click
  const handleDeleteClick = async (record: CleaningRecord) => {
    if (!confirm('¿Está seguro de eliminar este registro de limpieza?')) {
      return
    }

    try {
      const response = await fetch(`${API_BASE_URL}/equipment-cleanings/${record.id}`, {
        method: 'DELETE'
      })

      if (response.ok) {
        fetchCleaningRecords(selectedZone!.id)
      } else {
        const data = await response.json()
        setError(data.message || 'Error al eliminar el registro')
      }
    } catch (err) {
      setError('Error de conexión con el servidor')
      console.error('Error deleting cleaning record:', err)
    }
  }

  // Desplaza el filtro de fecha un día (parte de hoy si está vacío)
  const shiftDateFilter = (delta: number) => {
    const base = dateFilter || new Date().toLocaleDateString('sv-SE')
    const [y, m, d] = base.split('-').map(Number)
    const dt = new Date(y, m - 1, d)
    dt.setDate(dt.getDate() + delta)
    setDateFilter(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`)
  }

  // Opciones del filtro de elemento/equipo (de la zona seleccionada)
  const itemOptions = [
    ...(selectedZone?.elements || []).map(e => ({ key: `element:${e.id}`, label: e.name })),
    ...(selectedZone?.refrigerators || []).map(r => ({ key: `refrigerator:${r.id}`, label: r.name })),
  ]

  // Filtrado por fecha (scheduled_date) e item, y paginación cliente
  const filteredRecords = cleaningRecords.filter(record => {
    const dateMatch = !dateFilter || (record.scheduled_date || '').slice(0, 10) === dateFilter
    const recKey = record.item_type === 'element' ? `element:${record.element_id}` : `refrigerator:${record.refrigerator_id}`
    const itemMatch = itemFilter === 'all' || recKey === itemFilter
    return dateMatch && itemMatch
  })

  const totalPages = Math.ceil(filteredRecords.length / perPage)
  const paginatedRecords = filteredRecords.slice((currentPage - 1) * perPage, currentPage * perPage)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Limpieza y Desinfección por Zonas</h1>
          <p className="mt-1 text-sm text-gray-500">
            Selecciona una zona para gestionar la limpieza de sus elementos y equipos
          </p>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-400 hover:text-red-600"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Zone Selection */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Seleccionar Zona</h3>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
            {zones.map((zone) => (
              <button
                key={zone.id}
                onClick={() => fetchZoneDetails(zone.id)}
                className={`text-left p-4 border-2 rounded-lg transition-colors hover:border-primary-300 ${
                  selectedZone?.id === zone.id
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 bg-white'
                }`}
              >
                <div className="flex items-center">
                  <BuildingOfficeIcon className="h-6 w-6 text-gray-400 mr-3" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">{zone.name}</div>
                    <div className="text-xs text-gray-500">{zone.code} • {zone.type_name}</div>
                    {zone.has_overdue_cleaning && (
                      <div className="mt-1">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700">
                          {zone.overdue_cleaning_count} {zone.overdue_cleaning_count === 1 ? 'vencido' : 'vencidos'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Zone Details and Cleaning Tasks */}
      {selectedZone && (
        <div className="space-y-6">
          {/* Zone Info */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Zona: {selectedZone.name}
                </h3>
                <p className="text-sm text-gray-500">
                  {selectedZone.code} • {selectedZone.type_name}
                </p>
              </div>
              {selectedZone.has_overdue_cleaning && (
                <div className="text-right">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-50 text-red-700 border border-red-200">
                    <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
                    {selectedZone.overdue_cleaning_count} {selectedZone.overdue_cleaning_count === 1 ? 'vencido' : 'vencidos'}
                  </span>
                </div>
              )}
            </div>

            {selectedZone.description && (
              <p className="text-sm text-gray-600 mb-4">{selectedZone.description}</p>
            )}
          </div>

          {zoneLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Zone Elements */}
              <div className="bg-white shadow rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">
                    Elementos de la Zona ({selectedZone.elements?.length || 0})
                  </h3>
                </div>
                <div className="divide-y divide-gray-200">
                  {selectedZone.elements?.map((element) => (
                    <div key={element.id} className="p-6">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">
                            {element.name}
                          </h4>
                          <p className="text-xs text-gray-500">
                            {element.element_type_name} • {element.readable_frequency}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          {getPriorityBadge(element.priority)}
                          {getCleaningStatusBadge(element.cleaning_status)}
                        </div>
                      </div>

                      {element.cleaning_status === 'up_to_date' && element.next_cleaning_due && (
                        <p className="text-xs text-gray-500 mb-2">
                          Próxima limpieza: {fmtDate(element.next_cleaning_due)}
                        </p>
                      )}

                      <div className="flex justify-end items-center">
                        <button
                          onClick={() => openCreateModal('element', element.id, element.name)}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200"
                        >
                          <PlusIcon className="w-3 h-3 mr-1" />
                          Registrar Limpieza
                        </button>
                      </div>
                    </div>
                  ))}
                  {(!selectedZone.elements || selectedZone.elements.length === 0) && (
                    <div className="p-6 text-center text-gray-500 text-sm">
                      No hay elementos configurados en esta zona
                    </div>
                  )}
                </div>
              </div>

              {/* Refrigeration Equipment */}
              <div className="bg-white shadow rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">
                    Equipos de la Zona ({selectedZone.refrigerators?.length || 0})
                  </h3>
                </div>
                <div className="divide-y divide-gray-200">
                  {selectedZone.refrigerators?.map((equipment) => (
                    <div key={equipment.id} className="p-6">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">
                            {equipment.name}
                          </h4>
                          <p className="text-xs text-gray-500">
                            {equipment.code} • {equipment.type_name}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          {equipment.cleaning_frequency_name && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                              {equipment.cleaning_frequency_name}
                            </span>
                          )}
                          {getCleaningStatusBadge(equipment.cleaning_status)}
                        </div>
                      </div>

                      {equipment.cleaning_status === 'up_to_date' && equipment.next_cleaning_due && (
                        <p className="text-xs text-gray-500 mb-2">
                          Próxima limpieza: {fmtDate(equipment.next_cleaning_due)}
                        </p>
                      )}

                      <div className="flex justify-between items-center">
                        <div className="text-xs text-gray-500">
                          Estado: {equipment.status_name}
                        </div>
                        <button
                          onClick={() => openCreateModal('refrigerator', equipment.id, equipment.name)}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200"
                        >
                          <PlusIcon className="w-3 h-3 mr-1" />
                          Registrar Limpieza
                        </button>
                      </div>
                    </div>
                  ))}
                  {(!selectedZone.refrigerators || selectedZone.refrigerators.length === 0) && (
                    <div className="p-6 text-center text-gray-500 text-sm">
                      No hay equipos de refrigeración en esta zona
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Cleaning Records */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-3">
                Registros de Limpieza ({filteredRecords.length})
              </h3>
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Fecha</label>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => shiftDateFilter(-1)}
                      title="Día anterior"
                      className="p-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50"
                    >
                      <ChevronLeftIcon className="h-4 w-4" />
                    </button>
                    <DateInput
                      value={dateFilter}
                      onChange={(v) => setDateFilter(v)}
                      className="block w-36 border-gray-300 rounded-md shadow-sm text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => shiftDateFilter(1)}
                      title="Día siguiente"
                      className="p-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50"
                    >
                      <ChevronRightIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Elemento / Equipo</label>
                  <select
                    value={itemFilter}
                    onChange={(e) => setItemFilter(e.target.value)}
                    className="block w-56 border-gray-300 rounded-md shadow-sm text-sm"
                  >
                    <option value="all">Todos</option>
                    {itemOptions.map(o => (
                      <option key={o.key} value={o.key}>{o.label}</option>
                    ))}
                  </select>
                </div>
                {(dateFilter || itemFilter !== 'all') && (
                  <button
                    type="button"
                    onClick={() => { setDateFilter(''); setItemFilter('all') }}
                    className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
                  >
                    Limpiar
                  </button>
                )}
              </div>
            </div>
            {filteredRecords.length === 0 ? (
              <div className="p-6 text-center text-gray-500 text-sm">
                No hay registros de limpieza{cleaningRecords.length > 0 ? ' con esos filtros' : ' para esta zona'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Elemento/Equipo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tipo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fecha
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Estado
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Realizado por
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedRecords.map((record) => (
                      <tr key={record.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {record.item_name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {record.item_type === 'element' ? 'Elemento de zona' : 'Equipo de refrigeración'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                            {record.type}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {fmtDate(record.scheduled_date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(record.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {record.cleaned_by || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleViewDetails(record)}
                              className="text-primary-600 hover:text-primary-900"
                              title="Ver detalles"
                            >
                              <EyeIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => openEditModal(record)}
                              className="text-yellow-600 hover:text-yellow-900"
                              title="Editar"
                            >
                              <PencilIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(record)}
                              className="text-red-600 hover:text-red-900"
                              title="Eliminar"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {totalPages > 1 && (
                  <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200">
                    <span className="text-sm text-gray-700">
                      Página {currentPage} de {totalPages} ({filteredRecords.length} registros)
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                      >
                        Anterior
                      </button>
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                      >
                        Siguiente
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && selectedZone && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            <div className="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all max-w-2xl w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Registrar Limpieza - {selectedZone.name}
                  </h3>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Tipo de Limpieza *
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      required
                    >
                      <option value="routine">Rutinaria</option>
                      <option value="deep">Profunda</option>
                      <option value="sanitization">Sanitización</option>
                      <option value="disinfection">Desinfección</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Descripción *
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      placeholder="Describe la limpieza realizada..."
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Estado *
                      </label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                        required
                      >
                        <option value="scheduled">Programado</option>
                        <option value="in_progress">En Progreso</option>
                        <option value="completed">Completado</option>
                        <option value="cancelled">Cancelado</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Fecha Programada *
                      </label>
                      <DateInput
                        value={formData.scheduled_date}
                        onChange={(v) => setFormData({ ...formData, scheduled_date: v })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Fecha de Inicio
                      </label>
                      <DateInput
                        value={formData.started_at}
                        onChange={(v) => setFormData({ ...formData, started_at: v })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Fecha de Finalización
                      </label>
                      <DateInput
                        value={formData.completed_at}
                        onChange={(v) => setFormData({ ...formData, completed_at: v })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Realizado por
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <UserIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        value={formData.cleaned_by}
                        onChange={(e) => setFormData({ ...formData, cleaned_by: e.target.value })}
                        className="block w-full pl-10 border-gray-300 rounded-md shadow-sm"
                        placeholder="Nombre de quien realizó la limpieza"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Productos Utilizados
                    </label>
                    <textarea
                      value={formData.products_used}
                      onChange={(e) => setFormData({ ...formData, products_used: e.target.value })}
                      rows={2}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      placeholder="Lista los productos de limpieza utilizados..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Incidencias Encontradas
                    </label>
                    <textarea
                      value={formData.issues_found}
                      onChange={(e) => setFormData({ ...formData, issues_found: e.target.value })}
                      rows={2}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      placeholder="Describe las incidencias encontradas..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Soluciones Aplicadas
                    </label>
                    <textarea
                      value={formData.solutions_applied}
                      onChange={(e) => setFormData({ ...formData, solutions_applied: e.target.value })}
                      rows={2}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      placeholder="Describe las soluciones aplicadas..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Notas de Verificación
                    </label>
                    <textarea
                      value={formData.verification_notes}
                      onChange={(e) => setFormData({ ...formData, verification_notes: e.target.value })}
                      rows={3}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                      placeholder="Notas de verificación y control de calidad..."
                    />
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3">
                      <div className="flex">
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                        <div className="ml-3">
                          <p className="text-sm text-red-700">{error}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {saving ? 'Guardando...' : 'Registrar Limpieza'}
                </button>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {showViewModal && viewingRecord && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            <div className="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all max-w-2xl w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Detalles de Limpieza - {viewingRecord.item_name}
                  </h3>
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Tipo</label>
                      <p className="mt-1 text-sm text-gray-900">{viewingRecord.type}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Estado</label>
                      <div className="mt-1">{getStatusBadge(viewingRecord.status)}</div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Descripción</label>
                    <p className="mt-1 text-sm text-gray-900">{viewingRecord.description}</p>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Fecha Programada</label>
                      <p className="mt-1 text-sm text-gray-900">{fmtDate(viewingRecord.scheduled_date)}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Fecha Inicio</label>
                      <p className="mt-1 text-sm text-gray-900">{fmtDate(viewingRecord.started_at)}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Fecha Finalización</label>
                      <p className="mt-1 text-sm text-gray-900">{fmtDate(viewingRecord.completed_at)}</p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Realizado por</label>
                    <p className="mt-1 text-sm text-gray-900">{viewingRecord.cleaned_by || '-'}</p>
                  </div>

                  {viewingRecord.products_used && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Productos Utilizados</label>
                      <p className="mt-1 text-sm text-gray-900">{viewingRecord.products_used}</p>
                    </div>
                  )}

                  {viewingRecord.issues_found && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Incidencias Encontradas</label>
                      <p className="mt-1 text-sm text-gray-900">{viewingRecord.issues_found}</p>
                    </div>
                  )}

                  {viewingRecord.solutions_applied && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Soluciones Aplicadas</label>
                      <p className="mt-1 text-sm text-gray-900">{viewingRecord.solutions_applied}</p>
                    </div>
                  )}

                  {viewingRecord.verification_notes && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Notas de Verificación</label>
                      <p className="mt-1 text-sm text-gray-900">{viewingRecord.verification_notes}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4 text-xs text-gray-500">
                    <div>
                      <span className="font-medium">Creado:</span> {fmtDateTime(viewingRecord.created_at)}
                    </div>
                    <div>
                      <span className="font-medium">Actualizado:</span> {fmtDateTime(viewingRecord.updated_at)}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:w-auto sm:text-sm"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}